import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import DashboardSidebar from '@/components/DashboardSidebar';
import DashboardTopNav from '@/components/DashboardTopNav';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { HelpCircle, MessageCircle, Phone, Mail } from 'lucide-react';

const HelpCenter = () => {
  const { user } = useAuth();
  const helpOptions = [
    {
      title: "Live Chat Support",
      description: "Get instant help from our support team",
      icon: MessageCircle,
      availability: "Mon-Fri, 9:00-18:00 CET",
      action: "Start Chat"
    },
    {
      title: "Phone Support",
      description: "Speak directly with our experts",
      icon: Phone,
      availability: "Mon-Fri, 9:00-17:00 CET",
      action: "Call Now"
    },
    {
      title: "Email Support",
      description: "Send us your questions via email",
      icon: Mail,
      availability: "24/7 - Response within 24h",
      action: "Send Email"
    },
    {
      title: "FAQ",
      description: "Browse frequently asked questions",
      icon: HelpCircle,
      availability: "Available 24/7",
      action: "View FAQ"
    }
  ];

  const faqs = [
    {
      question: "How long does the Karta Pobytu application process take?",
      answer: "The processing time typically ranges from 1-3 months, depending on the voivodeship and application complexity."
    },
    {
      question: "What documents do I need for my application?",
      answer: "You'll need a valid passport, proof of income, housing documentation, health insurance, and completed application forms."
    },
    {
      question: "Can I work while my application is being processed?",
      answer: "Yes, you can continue working with your current permit while the Karta Pobytu application is being processed."
    },
    {
      question: "What happens if my application is rejected?",
      answer: "If rejected, you'll receive written notification with reasons. You can appeal the decision or submit a new application with corrected documentation."
    }
  ];

  // Content component to avoid duplication
  const HelpCenterContent = () => (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-foreground mb-4 animate-slide-in-right">
          Help Center
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '200ms' }}>
          Get the support you need for your Polish residence card application
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {helpOptions.map((option, index) => {
          const IconComponent = option.icon;
          return (
            <Card 
              key={index} 
              className="text-center hover:shadow-lg transition-all duration-300 hover:scale-105 animate-scale-in border-border"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <CardHeader>
                <div className="mx-auto p-3 bg-primary/10 rounded-full w-fit">
                  <IconComponent className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-lg text-foreground">{option.title}</CardTitle>
                <CardDescription className="text-muted-foreground">{option.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{option.availability}</p>
                <button className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-all duration-200 hover:scale-105 w-full">
                  {option.action}
                </button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="bg-card rounded-lg shadow-sm p-8 animate-fade-in border border-border" style={{ animationDelay: '600ms' }}>
        <h2 className="text-2xl font-bold text-foreground mb-8">Frequently Asked Questions</h2>
        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className="border-b border-border pb-6 last:border-b-0 animate-fade-in"
              style={{ animationDelay: `${(index + 4) * 150}ms` }}
            >
              <h3 className="text-lg font-semibold text-foreground mb-2">{faq.question}</h3>
              <p className="text-muted-foreground">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 bg-accent rounded-lg p-8 text-center animate-fade-in border border-border" style={{ animationDelay: '800ms' }}>
        <h3 className="text-xl font-bold text-foreground mb-4">Still Need Help?</h3>
        <p className="text-muted-foreground mb-6">
          Our experienced team is here to help you every step of the way. Don't hesitate to reach out for personalized assistance.
        </p>
        <button className="bg-primary text-primary-foreground px-8 py-3 rounded-lg hover:bg-primary/90 transition-all duration-200 hover:scale-105">
          Contact Our Expert Team
        </button>
      </div>
    </div>
  );

  return (
    <>
      {user ? (
        // Dashboard layout for logged-in users
        <SidebarProvider>
          <div className="min-h-screen flex w-full bg-background">
            <DashboardSidebar />
            <SidebarInset className="flex-1 flex flex-col">
              <DashboardTopNav pageTitle="Help Center" />
              <main className="flex-1 p-4 sm:p-8 mt-[3.5rem] sm:mt-[4.2rem] overflow-auto">
                <HelpCenterContent />
              </main>
            </SidebarInset>
          </div>
        </SidebarProvider>
      ) : (
        // Regular layout for non-logged-in users
        <div className="min-h-screen bg-background flex flex-col">
          <Header />
          <div className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fade-in">
            <HelpCenterContent />
          </div>
          <Footer />
        </div>
      )}
    </>
  );
};

export default HelpCenter;
